import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../lib/auth";
import {
  browserNotificationPromptDelay,
  readBrowserNotificationPermission,
  rememberBrowserNotificationPromptSeen,
  shouldShowBrowserNotificationPrompt,
  useBrowserNotifications,
} from "../../lib/browserNotifications";
import { Badge, Button, Modal, useToast } from "../ui";

const HIGHLIGHTS = [
  "Alertes Monde Vivant, boss, donjons et evenements serveur.",
  "Ventes importantes, annonces et patchs majeurs visibles sans rester sur la page.",
  "Controle gardien : tu peux refuser, et la demande revient seulement une fois par heure.",
];

export function WindowsNotificationPrompt() {
  const { isAuthenticated, isLoading } = useAuth();
  const notifications = useBrowserNotifications();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState(0);
  const [pending, setPending] = useState(false);
  const [requestIssue, setRequestIssue] = useState<string | null>(null);

  const canAsk =
    !isLoading &&
    isAuthenticated &&
    notifications.isSupported &&
    notifications.isSecureContext &&
    !notifications.isGranted;

  useEffect(() => {
    if (!canAsk) return;

    const openPrompt = () => {
      if (!shouldShowBrowserNotificationPrompt()) return;
      rememberBrowserNotificationPromptSeen();
      setOpen(true);
    };

    const delay = browserNotificationPromptDelay();
    const timer = window.setTimeout(openPrompt, Number.isFinite(delay) ? delay : 0);
    const onVisibility = () => {
      if (document.visibilityState === "visible") openPrompt();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [canAsk, notifications.permission, seed]);

  const blockedText = useMemo(() => {
    if (notifications.isDenied) {
      return "Ton navigateur bloque deja les notifications. Clique sur le cadenas de l'adresse ether-ia.fr, autorise les notifications, puis verifie ici.";
    }
    return null;
  }, [notifications.isDenied]);

  const closeForNow = () => {
    rememberBrowserNotificationPromptSeen();
    setRequestIssue(null);
    setOpen(false);
    setSeed((value) => value + 1);
  };

  const activate = async () => {
    setRequestIssue(null);
    setPending(true);
    try {
      const result = await notifications.requestPermission();
      if (result.permission === "granted") {
        notifications.sendPreviewNotification();
        toast.success("Notifications activees", "Etheria pourra maintenant t'alerter depuis Windows.");
        setOpen(false);
      } else if (result.permission === "denied") {
        toast.error("Notifications bloquees", "Autorise ether-ia.fr dans les reglages du navigateur, puis reviens verifier.");
        closeForNow();
      } else if (result.outcome === "timeout") {
        const message =
          "Edge n'a ouvert aucune demande. Clique sur le cadenas a gauche de l'adresse, autorise les notifications, puis reessaie.";
        setRequestIssue(message);
        toast.error("Demande non ouverte", message);
      } else if (result.outcome === "error") {
        const message = "Le navigateur a refuse d'ouvrir la permission. Recharge la page ou autorise-la depuis le cadenas de l'adresse.";
        setRequestIssue(message);
        toast.error("Activation impossible", message);
      } else {
        toast.info("Pas maintenant", "La demande reviendra plus tard si tu ne les actives pas.");
        closeForNow();
      }
    } finally {
      setPending(false);
      setSeed((value) => value + 1);
    }
  };

  const verify = () => {
    notifications.refresh();
    if (readBrowserNotificationPermission() === "granted") {
      toast.success("Notifications actives", "La permission est bien ouverte.");
      setOpen(false);
    } else {
      toast.error("Toujours bloque", "Le navigateur n'a pas encore redonne la permission a Etheria.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeForNow}
      title="Reste branche au royaume"
      subtitle="Active les notifications Windows d'Etheria"
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={closeForNow}>
            Plus tard
          </Button>
          {notifications.isDenied ? (
            <Button type="button" variant="secondary" onClick={verify}>
              Verifier la permission
            </Button>
          ) : (
            <Button type="button" variant="primary" loading={pending} onClick={() => void activate()}>
              Activer les notifications
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-gold-400/25 bg-gradient-to-br from-gold-500/12 via-obsidian-200/70 to-arcane-500/10 p-4 shadow-glow-gold">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-display text-display-sm text-parchment-100 text-engraved">Ne rate plus les moments importants</p>
              <p className="mt-2 text-sm leading-6 text-parchment-200/70">
                Une alerte discrete quand le serveur bouge vraiment : evenement, donjon, annonce, vente ou patch important.
              </p>
            </div>
            <Badge tone={notifications.isDenied ? "blood" : "gold"} dot seal>
              {notifications.statusLabel}
            </Badge>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {HIGHLIGHTS.map((text, index) => (
            <div key={text} className="rounded-lg border border-bronze-600/25 bg-obsidian-300/55 p-3">
              <p className="text-xs uppercase tracking-[0.22em] text-gold-300/80">Signal {index + 1}</p>
              <p className="mt-2 text-sm leading-5 text-parchment-200/72">{text}</p>
            </div>
          ))}
        </div>

        {blockedText && (
          <div className="rounded-lg border border-blood-400/35 bg-blood-500/12 p-3 text-sm leading-6 text-blood-100">
            {blockedText}
          </div>
        )}

        {requestIssue && !blockedText && (
          <div className="rounded-lg border border-blood-400/35 bg-blood-500/12 p-3 text-sm leading-6 text-blood-100">
            {requestIssue}
          </div>
        )}

        <p className="text-xs leading-5 text-parchment-200/45">
          Etheria ne peut pas activer Windows sans ton clic. Tant que ce n'est pas accepte, le rappel reviendra au maximum une fois par heure.
        </p>
      </div>
    </Modal>
  );
}
